<template>
  <swiper class="w-100 h-fit" ref="signUpSwiper" :modules="[Navigation]" @swiper="onSwiper">
    <swiper-slide class="swiper-no-swiping">
      <ion-card class="ion-padding bg-white no-shadow border-default w-100 max-w-d">
        <form @submit="onSubmit">
          <ion-col size-md="12" size-lg="12">
            <h3 class="h3 text-color-body">{{ t('signUp.passwordTitle') }}</h3>
            <p class="text-color-body text-align-left font-xs mt-xs">
              {{ t('signUp.passwordDescription') }}
            </p>
            <ul class="font-xs">
              <li>{{ t('signUp.passwordDescriptionLeast.minCharacters') }}</li>
              <li>{{ t('signUp.passwordDescriptionLeast.uppercase') }}</li>
              <li>{{ t('signUp.passwordDescriptionLeast.lowercase') }}</li>
              <li>{{ t('signUp.passwordDescriptionLeast.number') }}</li>
              <li>{{ t('signUp.passwordDescriptionLeast.special') }}</li>
            </ul>
            <div class="mt-lg">
              <ion-input
                type="password"
                name="password"
                v-model="password"
                v-bind="passwordAttrs"
                class="custom-field"
                :label="t('login.labels.password')"
                :placeholder="t('login.labels.password')"
                label-placement="stacked"
                fill="outline"
                mode="md"
                shape="round"
              >
                <ion-icon
                  slot="start"
                  :icon="lockClosedOutline"
                  inner="true"
                  color="primary"
                ></ion-icon>
                <ion-input-password-toggle slot="end"></ion-input-password-toggle
              ></ion-input>
              <ion-text color="error">{{ errors.password }}</ion-text>
            </div>
          </ion-col>

          <ion-row>
            <ion-col size="12">
              <ion-button
                :disabled="isLoading || isGeneralLoading"
                fill="solid"
                class="text-sm"
                color="primary"
                expand="block"
                size="default"
                type="submit"
              >
                {{ t('actions.createAccount') }}
              </ion-button>
            </ion-col>
          </ion-row>
        </form>
        <ion-progress-bar v-if="isLoading" type="indeterminate" class="mt-xs"></ion-progress-bar>
      </ion-card>
    </swiper-slide>
    <swiper-slide class="swiper-no-swiping">
      <div v-if="organizationData">
        <CompletedVerificationAbroad :organization-data="organizationData" />
      </div>
    </swiper-slide>
  </swiper>
</template>

<script setup lang="ts">
  import {
    IonButton,
    IonCol,
    IonRow,
    IonProgressBar,
    IonCard,
    IonInput,
    IonText,
    IonIcon,
    IonInputPasswordToggle,
  } from '@ionic/vue';
  import { Navigation } from 'swiper/modules';
  import { useI18n } from 'vue-i18n';
  import { ref } from 'vue';
  import { useForm } from 'vee-validate';
  import * as Yup from 'yup';
  import { lockClosedOutline } from 'ionicons/icons';
  import { Swiper, SwiperSlide } from 'swiper/vue';
  import CompletedVerificationAbroad from './CompletedVerificationAbroad.vue';
  import { useProfile } from '@/composables/useProfile';

  withDefaults(
    defineProps<{
      isGeneralLoading?: boolean;
      organizationData?: any;
    }>(),
    {},
  );

  const { t } = useI18n();
  const isLoading = ref(false);
  const swiperAbroad = ref();
  const { handleChangeCurrentPassword } = useProfile();

  function onSwiper(swiper: any) {
    swiperAbroad.value = swiper;
  }

  const { defineField, handleSubmit, errors } = useForm({
    validationSchema: {
      password: Yup.string()
        .required(t('rules.required'))
        .min(8, t('validations.minLength', { min: 8 }))
        .matches(/[A-Z]/, t('validations.uppercase'))
        .matches(/[a-z]/, t('validations.lowercase'))
        .matches(/[0-9]/, t('validations.number'))
        .matches(/[!@#$%^&*(),.?":{}|<>]/, t('validations.specialChar')),
    },
  });

  const [password, passwordAttrs] = defineField('password');

  const onSubmit = handleSubmit(async (values) => {
    isLoading.value = true;
    const response = await handleChangeCurrentPassword(values.password);
    if (response) {
      swiperAbroad.value?.slideTo(1);
    }
    isLoading.value = false;
  });
</script>
<style scoped lang="scss">
  .swiper {
    width: 100% !important;
    height: 100%;

    .swiper-slide {
      align-items: end;
    }
  }
  .max-w-d {
    max-width: 413px;
  }
</style>
